import { useState, useEffect, useCallback } from "react";

const principles = [
  {
    number: "01",
    title: "The teacher leads;\nAI assists.",
    body: "Every AI output is a draft. The teacher reviews, edits, accepts. The platform handles the repetitive work so the teacher can focus on the human work — reading alongside students, giving feedback face to face, making the judgment calls that only a person in the room can make.",
    accent: "#E8E8E8",
  },
  {
    number: "02",
    title: "Ready in ten.\nBring everything you've got.",
    body: "Scrambling before class? Drop in a text and you're teaching in minutes. Sitting on years of materials — PDFs, slides, documents, notes? Bring it all. The platform meets you wherever you are.",
    accent: "#D4D4D4",
  },
  {
    number: "03",
    title: "In the classroom\nor across the world.",
    body: "Everything works equally well in person and remote, synchronous and asynchronous. A live lesson with a teacher controlling the screen, a homework assignment completed at midnight, a tutoring session over video — the platform never forces a delivery mode.",
    accent: "#C0C0C0",
  },
  {
    number: "04",
    title: "One place,\nnot ten tabs.",
    body: "Books, passages, assessments, vocabulary, schedules, maps, homework, feedback — it all lives here. No more toggling between a drive folder, a grading app, a flashcard site, and a spreadsheet.",
    accent: "#ACACAC",
  },
  {
    number: "05",
    title: "Zoom in,\nbranch out.",
    body: "Navigate vertically from book to chapter to passage to sentence to word. Navigate horizontally from a passage to a map location, a historical concept, a vocabulary list, a parallel text in another course. Everything is interconnected — the way knowledge actually works.",
    accent: "#989898",
  },
  {
    number: "06",
    title: "Protect\nthe deep work.",
    body: "The platform nurtures contemplative reading and distraction-free writing. Print it out, hold the book, write by hand, or read and write on screen. The platform is never hostile to paper — it embraces the physical-digital bridge as a natural part of how people read and write.",
    accent: "#848484",
  },
];

const AUTOPLAY_INTERVAL = 7000;

export default function PrinciplesCarousel() {
  const [active, setActive] = useState(0);
  const [direction, setDirection] = useState(1);
  const [isPaused, setIsPaused] = useState(false);
  const [progress, setProgress] = useState(0);

  const goTo = useCallback(
    (index) => {
      setDirection(index > active ? 1 : -1);
      setActive(index);
      setProgress(0);
    },
    [active]
  );

  const next = useCallback(() => {
    setDirection(1);
    setActive((prev) => (prev + 1) % principles.length);
    setProgress(0);
  }, []);

  const prev = useCallback(() => {
    setDirection(-1);
    setActive((prev) => (prev - 1 + principles.length) % principles.length);
    setProgress(0);
  }, []);

  // Autoplay
  useEffect(() => {
    if (isPaused) return;
    const interval = setInterval(() => {
      next();
    }, AUTOPLAY_INTERVAL);
    return () => clearInterval(interval);
  }, [isPaused, next]);

  // Progress bar animation
  useEffect(() => {
    if (isPaused) return;
    setProgress(0);
    const start = Date.now();
    let raf;
    const tick = () => {
      const elapsed = Date.now() - start;
      setProgress(Math.min(elapsed / AUTOPLAY_INTERVAL, 1));
      if (elapsed < AUTOPLAY_INTERVAL) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [active, isPaused]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [next, prev]);

  const current = principles[active];

  return (
    <div
      className="relative flex flex-col items-center justify-center flex-1 w-full select-none"
      style={{ cursor: "default" }}
    >
      {/* Subtle background gradient */}
      <div
        className="absolute inset-0 pointer-events-none transition-all duration-1000"
        style={{
          background: `radial-gradient(ellipse at 30% 50%, ${current.accent}08 0%, transparent 60%), radial-gradient(ellipse at 70% 80%, ${current.accent}05 0%, transparent 50%)`,
        }}
      />

      {/* Main content */}
      <div className="relative z-[1] w-full max-w-[800px] flex flex-col items-center gap-12">
        {/* Number */}
        <div className="relative">
          <span
            key={`num-${active}`}
            className="animate-fadeSlideIn"
            style={{
              fontSize: 120,
              fontWeight: 300,
              lineHeight: 1,
              color: "transparent",
              WebkitTextStroke: `1.5px ${current.accent}40`,
              fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
              letterSpacing: "-0.02em",
            }}
          >
            {current.number}
          </span>
          <span
            style={{
              position: "absolute",
              bottom: -4,
              right: -20,
              fontSize: 13,
              color: "#555",
              fontFamily: "'SF Mono', 'Fira Code', 'Consolas', monospace",
            }}
          >
            /06
          </span>
        </div>

        {/* Title */}
        <h2
          key={`title-${active}`}
          className="animate-fadeSlideIn text-center m-0 whitespace-pre-line"
          style={{
            fontSize: "clamp(28px, 5vw, 48px)",
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: "-0.01em",
            fontFamily: "'Georgia', 'Times New Roman', 'Garamond', serif",
            color: "#FAFAFA",
            animationDelay: "0.1s",
          }}
        >
          {current.title}
        </h2>

        {/* Divider */}
        <div
          className="transition-all duration-800"
          style={{
            width: 48,
            height: 1,
            background: `linear-gradient(90deg, transparent, ${current.accent}60, transparent)`,
          }}
        />

        {/* Body */}
        <p
          key={`body-${active}`}
          className="animate-fadeSlideIn text-center max-w-[580px] m-0"
          style={{
            fontSize: "clamp(15px, 2vw, 18px)",
            lineHeight: 1.7,
            color: "#999",
            fontWeight: 400,
            fontFamily: "'Georgia', 'Times New Roman', 'Garamond', serif",
            animationDelay: "0.2s",
          }}
        >
          {current.body}
        </p>
      </div>

      {/* Navigation */}
      <div className="mt-10 flex flex-col items-center gap-5 z-[2]">
        {/* Progress dots */}
        <div className="flex gap-1.5 items-center">
          {principles.map((_, i) => (
            <button
              key={i}
              onClick={() => goTo(i)}
              className="border-none cursor-pointer p-0 relative overflow-hidden rounded"
              style={{
                width: i === active ? 32 : 8,
                height: 8,
                background: i === active ? "transparent" : "#333",
                transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
              }}
              aria-label={`Go to principle ${i + 1}`}
            >
              {i === active && (
                <>
                  <div className="absolute inset-0 rounded bg-[#333]" />
                  <div
                    className="absolute top-0 left-0 bottom-0 rounded"
                    style={{
                      width: `${progress * 100}%`,
                      background: current.accent,
                      transition: isPaused ? "none" : "width 0.1s linear",
                    }}
                  />
                </>
              )}
            </button>
          ))}
        </div>

        {/* Arrow buttons */}
        <div className="flex items-center gap-3">
          <button
            onClick={prev}
            className="w-11 h-11 rounded-full border border-[#333] bg-transparent text-[#888] cursor-pointer flex items-center justify-center text-lg transition-all duration-200 hover:border-[#666] hover:text-white"
            style={{ fontFamily: "system-ui" }}
            aria-label="Previous principle"
          >
            ←
          </button>
          <button
            onClick={() => setIsPaused((p) => !p)}
            className="w-11 h-11 rounded-full border border-[#333] bg-transparent text-[#888] cursor-pointer flex items-center justify-center text-base transition-all duration-200 hover:border-[#666] hover:text-white"
            style={{ fontFamily: "system-ui" }}
            aria-label={isPaused ? "Resume autoplay" : "Pause autoplay"}
          >
            {isPaused ? "▶" : "⏸"}
          </button>
          <button
            onClick={next}
            className="w-11 h-11 rounded-full border border-[#333] bg-transparent text-[#888] cursor-pointer flex items-center justify-center text-lg transition-all duration-200 hover:border-[#666] hover:text-white"
            style={{ fontFamily: "system-ui" }}
            aria-label="Next principle"
          >
            →
          </button>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes fadeSlideIn {
          from {
            opacity: 0;
            transform: translateY(12px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        .animate-fadeSlideIn {
          animation: fadeSlideIn 0.6s ease both;
        }
      `}</style>
    </div>
  );
}
